import type { ReactNode } from 'react';
import { usePerformance } from '@/context/PerformanceContext';
import type { DerivedPerformance } from '@/utils/performanceData';

interface PerformanceBoundaryProps {
  /** Rendered once the external data has loaded successfully. */
  children: (data: DerivedPerformance) => ReactNode;
  /** Optional heading shown in the loading / error states. */
  label?: string;
}

/**
 * Small gate around any block that depends on the external performance data.
 * It shows a skeleton while the file is loading and a clear, retryable message
 * if the file is missing or malformed — the page never gets stuck or blank.
 */
export default function PerformanceBoundary({ children, label = 'live trading data' }: PerformanceBoundaryProps) {
  const { status, error, data, reload } = usePerformance();

  if (status === 'ready' && data) {
    return <>{children(data)}</>;
  }

  if (status === 'error') {
    return (
      <div className="rounded-lg border border-accent-200 bg-accent-50/60 p-6 md:p-8 text-center">
        <span className="mx-auto w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-900">
          <i className="ri-error-warning-line text-xl" aria-hidden="true"></i>
        </span>
        <h3 className="mt-4 font-heading text-base md:text-lg font-semibold text-foreground-950">
          Couldn&apos;t load the {label}
        </h3>
        <p className="mx-auto mt-2 max-w-md text-xs md:text-sm leading-relaxed text-foreground-600">
          {error}
        </p>
        <button
          type="button"
          onClick={reload}
          className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line" aria-hidden="true"></i>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-background-200 bg-background-100/60 p-6 md:p-8" aria-busy="true">
      <div className="flex items-center justify-center gap-3 text-sm text-foreground-500">
        <i className="ri-loader-4-line animate-spin text-lg" aria-hidden="true"></i>
        Loading {label}…
      </div>
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
        {[0, 1, 2, 3].map((key) => (
          <div key={key} className="h-24 rounded-lg bg-background-200/60 animate-pulse"></div>
        ))}
      </div>
    </div>
  );
}